import React from 'react';
// Direct path, NOT the '@/components' barrel: the barrel re-exports Layout,
// which pulls Sidebar → react-router-dom, and CRA's jest resolver cannot read
// that package's ESM exports map. A leaf component has no business dragging the
// whole component tree into every test that touches it.
import { ImagePreview } from '@/components/ImagePreview';

/**
 * The shipment module's photo strip: thumbnails that open in the shared
 * lightbox. Every place the drawer shows driver-captured photos renders through
 * here — timeline attachments, 4W start/end evidence, and per-item POD — so the
 * tile size, border and empty state stay in one place instead of drifting apart
 * across three copies.
 *
 *   urls: []        →  emptyLabel ? <italic note> : null
 *   urls: [a, b, c] →  [img][img][img]  (wraps, each click-to-zoom)
 *
 * Variants:
 *   photo      56 × 56, object-cover — a square crop reads fine for photos.
 *   signature  112 × 56, object-contain on fixed white — a signature is wide
 *              and must never be cropped; black ink stays legible in dark mode.
 */
export type PhotoTileVariant = 'photo' | 'signature';

const TILE_CLASS: Record<PhotoTileVariant, string> = {
  photo: 'h-14 w-14 rounded-md border border-stroke-soft-200 object-cover',
  signature:
    'h-14 w-28 rounded-md border border-stroke-soft-200 bg-static-white object-contain',
};

// The lightbox sits on a dark scrim; give a (possibly transparent) signature
// PNG the same white backing it has as a tile.
const PREVIEW_CLASS: Record<PhotoTileVariant, string> = {
  photo: '',
  signature: 'bg-static-white',
};

interface PhotoTileGridProps {
  urls: string[];
  /** Alt prefix; each tile is captioned "{alt} {n}". */
  alt: string;
  /**
   * Note rendered when there are no photos. Omit when the caller already
   * guarantees a non-empty list (then an empty list renders nothing).
   */
  emptyLabel?: string;
  /** Extra classes on the grid wrapper — spacing is the caller's business. */
  className?: string;
  /** Tile shape; defaults to the square photo tile. */
  variant?: PhotoTileVariant;
}

const PhotoTileGrid: React.FC<PhotoTileGridProps> = ({
  urls,
  alt,
  emptyLabel,
  className = '',
  variant = 'photo',
}) => {
  if (urls.length === 0) {
    if (!emptyLabel) return null;
    return (
      <div className="mt-0.5 text-[11px] italic text-text-soft-400">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`.trim()}>
      {urls.map((url, i) => (
        <ImagePreview
          key={`${url}-${i}`}
          src={url}
          alt={`${alt} ${i + 1}`}
          buttonClassName="shrink-0"
          className={TILE_CLASS[variant]}
          previewClassName={PREVIEW_CLASS[variant]}
        />
      ))}
    </div>
  );
};

export default PhotoTileGrid;
